/**
 * MobileLinkPlaybook Component
 *
 * Mobile page for linking existing playbooks to a strategy.
 * Route: /settings/strategies/:strategyId/playbooks/link
 *
 * Supports multi-select with search filtering and active/archived sections.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Eye, EyeOff, Search } from 'lucide-react';

import {
  Button,
  Input,
  Checkbox,
  Alert,
  Breadcrumbs,
  BreadcrumbItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
} from '@enfomentis/ui';
import { playbookService } from '@/services/PlaybookService';
import type { Playbook } from '@/types/schema/Playbook';
import { useStrategiesPlaybooks } from '../context';

/** Sort alphabetically by name (case-insensitive). Returns a new sorted array. */
const sortByName = <T extends { name: string }>(items: T[]): T[] =>
  [...items].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );

export const MobileLinkPlaybook: React.FC = () => {
  const { strategyId } = useParams<{ strategyId: string }>();
  const navigate = useNavigate();
  const emptyModal = useDisclosure();
  const hasCheckedEmpty = useRef(false);

  // Context for data and linking
  const {
    strategies,
    loading: contextLoading,
    selectedStrategy,
    selectedStrategyId,
    showArchived: showArchivedStrategies,
    setShowArchived: setShowArchivedStrategies,
    selectStrategy,
    linkPlaybooks,
  } = useStrategiesPlaybooks();

  // Data state
  const [allPlaybooks, setAllPlaybooks] = useState<Playbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Load all playbooks
   */
  const loadPlaybooks = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const playbooks = await playbookService.getAll();
      setAllPlaybooks(playbooks);
    } catch (error) {
      console.error('Failed to load playbooks:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load playbooks on mount
  useEffect(() => {
    loadPlaybooks();
  }, [loadPlaybooks]);

  // Validate and sync URL param to context on mount/change
  useEffect(() => {
    // Wait for strategies to load before any validation
    if (contextLoading) {
      return;
    }

    if (!strategyId) {
      navigate('/settings/strategies', { replace: true });
      return;
    }

    // Validate against full strategies array (not filtered)
    const strategy = strategies.find((s) => s.id === strategyId);

    if (!strategy) {
      // Strategy not found - redirect to list
      navigate('/settings/strategies', { replace: true });
      return;
    }

    // Auto-enable showArchivedStrategies if strategy is archived and filter is off
    if (strategy.status === 'archived' && !showArchivedStrategies) {
      setShowArchivedStrategies(true);
    }

    // Auto-enable showArchived (local) if strategy is archived
    if (strategy.status === 'archived') {
      setShowArchived(true);
    }

    // Sync to context if not already selected
    if (selectedStrategyId !== strategyId) {
      selectStrategy(strategyId);
    }
  }, [strategyId, strategies, contextLoading, selectedStrategyId, showArchivedStrategies, setShowArchivedStrategies, selectStrategy, navigate]);

  // Filter out playbooks already linked to this strategy
  const linkablePlaybooks = useMemo(() => {
    const currentIdSet = new Set(selectedStrategy?.playbookIds || []);
    return allPlaybooks.filter(p => !currentIdSet.has(p.id));
  }, [allPlaybooks, selectedStrategy?.playbookIds]);

  // Apply search filter (case-insensitive name match)
  const filteredPlaybooks = useMemo(() => {
    if (!searchQuery.trim()) {
      return linkablePlaybooks;
    }
    const query = searchQuery.toLowerCase().trim();
    return linkablePlaybooks.filter(p =>
      p.name.toLowerCase().includes(query)
    );
  }, [linkablePlaybooks, searchQuery]);

  // Group into active and archived
  const activePlaybooks = useMemo(
    () => sortByName(filteredPlaybooks.filter((p) => p.status === 'active')),
    [filteredPlaybooks]
  );

  const archivedPlaybooks = useMemo(
    () => sortByName(filteredPlaybooks.filter((p) => p.status === 'archived')),
    [filteredPlaybooks]
  );

  // Check if there are any linkable playbooks at all
  const hasLinkablePlaybooks = linkablePlaybooks.length > 0;

  // Show empty modal ONCE after load completes if nothing linkable
  useEffect(() => {
    if (!loading && !loadError && !hasLinkablePlaybooks && !hasCheckedEmpty.current) {
      hasCheckedEmpty.current = true;
      emptyModal.onOpen();
    }
  }, [loading, loadError, hasLinkablePlaybooks, emptyModal]);

  /**
   * Toggle selection of a playbook
   */
  const handleToggleSelection = useCallback((playbookId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(playbookId)) {
        next.delete(playbookId);
      } else {
        next.add(playbookId);
      }
      return next;
    });
  }, []);

  /**
   * Handle link button click
   */
  const handleLink = useCallback(async () => {
    if (selectedIds.size === 0) return;

    try {
      setIsSubmitting(true);
      // Context's linkPlaybooks already shows toast - no duplicate
      await linkPlaybooks(Array.from(selectedIds));
      navigate(`/settings/strategies/${strategyId}`);
    } catch (error) {
      console.error('Failed to link playbooks:', error);
      // Error toast shown by context
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedIds, linkPlaybooks, navigate, strategyId]);

  /**
   * Handle navigation to Settings
   */
  const handleNavigateToSettings = useCallback(() => {
    navigate('/settings');
  }, [navigate]);

  /**
   * Handle navigation to Strategies list
   */
  const handleNavigateToStrategies = useCallback(() => {
    navigate('/settings/strategies');
  }, [navigate]);

  /**
   * Handle cancel/back (to strategy detail)
   */
  const handleCancel = useCallback(() => {
    navigate(`/settings/strategies/${strategyId}`);
  }, [navigate, strategyId]);

  /**
   * Handle "Create New" from empty modal
   */
  const handleCreateNew = useCallback(() => {
    emptyModal.onClose();
    navigate(`/settings/strategies/${strategyId}/playbooks/new`);
  }, [emptyModal, navigate, strategyId]);

  /**
   * Handle cancel from empty modal
   */
  const handleEmptyCancel = useCallback(() => {
    emptyModal.onClose();
    navigate(`/settings/strategies/${strategyId}`);
  }, [emptyModal, navigate, strategyId]);

  /**
   * Render a playbook item with checkbox
   */
  const renderPlaybookItem = useCallback((playbook: Playbook) => (
    <div
      key={playbook.id}
      className="flex items-center gap-3 px-3 py-3 rounded-md hover:bg-default-100 active:bg-default-200 cursor-pointer w-full overflow-hidden min-w-0"
      onClick={() => handleToggleSelection(playbook.id)}
    >
      {/* Stop propagation to prevent double-toggle when clicking checkbox directly */}
      <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          isSelected={selectedIds.has(playbook.id)}
          onValueChange={() => handleToggleSelection(playbook.id)}
          aria-label={`Select ${playbook.name}`}
        />
      </div>
      <span className={`min-w-0 flex-1 truncate ${
        playbook.status === 'archived' ? 'text-text-tertiary' : 'text-text-primary'
      }`}>
        {playbook.name}
      </span>
      {playbook.status === 'archived' && (
        <span className="ml-2 shrink-0 text-xs text-text-tertiary bg-default-100 px-1.5 py-0.5 rounded">
          Archived
        </span>
      )}
    </div>
  ), [selectedIds, handleToggleSelection]);

  // Show loading state while context data loads
  if (contextLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-text-secondary">Loading...</span>
      </div>
    );
  }

  // Strategy not found handled by useEffect redirect
  if (!selectedStrategy) {
    return null;
  }

  return (
    <Card radius="none" shadow="none" className="h-full flex flex-col">
      {/* Header - Breadcrumb Navigation + Page Title */}
      <CardHeader className="flex-col items-start pt-1 pb-1 border-b border-default-200">
        <div className="flex items-center">
          <Breadcrumbs variant="light" size="sm">
            <BreadcrumbItem
              onPress={handleNavigateToSettings}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Settings
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleNavigateToStrategies}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              Strategies
            </BreadcrumbItem>
            <BreadcrumbItem
              onPress={handleCancel}
              className="text-text-secondary hover:text-text-primary"
              isCurrent={false}
            >
              {selectedStrategy.name}
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>
        <h1 className="text-lg font-semibold text-text-primary mt-1">Link Playbook</h1>
      </CardHeader>

      {/* Content */}
      <CardBody className="flex-1 overflow-y-auto p-0">
        {/* Search & Filter Bar */}
        {hasLinkablePlaybooks && (
          <div className="px-4 py-3 border-b border-default-200 space-y-3">
            <Input
              placeholder="Search playbooks..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              startContent={<Search size={16} className="text-text-tertiary" />}
              isClearable
              onClear={() => setSearchQuery('')}
              className="[&_input]:text-base"
            />

            {/* Archive Toggle */}
            <div className="flex items-center justify-between">
              <Button
                size="sm"
                variant="light"
                startContent={showArchived ? <Eye size={16} /> : <EyeOff size={16} />}
                onPress={() => setShowArchived(prev => !prev)}
              >
                {showArchived ? 'Showing Archived' : 'Show Archived'}
              </Button>
              {!showArchived && archivedPlaybooks.length > 0 && (
                <span className="text-sm text-text-tertiary">
                  {archivedPlaybooks.length} archived
                </span>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-8">
            <p className="text-text-secondary">Loading playbooks...</p>
          </div>
        )}

        {/* Error State */}
        {loadError && !loading && (
          <div className="p-4 space-y-2">
            <Alert color="danger" variant="flat">
              Failed to load playbooks. Please try again.
            </Alert>
            <Button size="sm" variant="flat" onPress={loadPlaybooks}>
              Retry
            </Button>
          </div>
        )}

        {/* Playbook List - NO Card wrapping on checkbox rows to avoid nested pressable conflicts */}
        {!loading && !loadError && hasLinkablePlaybooks && (
          <div className="py-2">
            {/* Active Section */}
            {activePlaybooks.length > 0 && (
              <div className="px-4 py-2">
                <h4 className="text-sm font-medium text-text-secondary mb-2">
                  Active ({activePlaybooks.length})
                </h4>
                <div className="space-y-1">
                  {activePlaybooks.map(renderPlaybookItem)}
                </div>
              </div>
            )}

            {/* Archived Section */}
            {showArchived && archivedPlaybooks.length > 0 && (
              <div className="px-4 py-2 border-t border-default-200">
                <h4 className="text-sm font-medium text-text-tertiary mb-2">
                  Archived ({archivedPlaybooks.length})
                </h4>
                <div className="space-y-1">
                  {archivedPlaybooks.map(renderPlaybookItem)}
                </div>
              </div>
            )}

            {/* No results from search */}
            {filteredPlaybooks.length === 0 && searchQuery.trim() && (
              <div className="text-center py-8">
                <p className="text-text-secondary">
                  No playbooks match "{searchQuery}"
                </p>
              </div>
            )}

            {/* Hint when results are only in archived */}
            {activePlaybooks.length === 0 && archivedPlaybooks.length > 0 && !showArchived && !searchQuery.trim() && (
              <div className="text-center py-8">
                <p className="text-text-secondary">
                  All linkable playbooks are archived
                </p>
              </div>
            )}
          </div>
        )}

        {/* Empty state - no linkable playbooks (modal will show) */}
        {!loading && !loadError && !hasLinkablePlaybooks && (
          <div className="text-center py-8">
            <p className="text-text-secondary">
              No playbooks available to link.
            </p>
          </div>
        )}
      </CardBody>

      {/* Footer */}
      <CardFooter className="flex items-center justify-between pt-2 border-t border-default-200">
        <Button
          variant="flat"
          size="sm"
          onPress={handleCancel}
          isDisabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          size="sm"
          onPress={handleLink}
          isDisabled={selectedIds.size === 0}
          isLoading={isSubmitting}
        >
          Link Selected ({selectedIds.size})
        </Button>
      </CardFooter>

      {/* Empty State Modal */}
      <Modal
        isOpen={emptyModal.isOpen}
        onClose={handleEmptyCancel}
        size="sm"
        placement="center"
      >
        <ModalContent>
          <ModalHeader>No Playbooks Available</ModalHeader>
          <ModalBody>
            <p className="text-text-secondary">
              All playbooks are already linked to this strategy, or no playbooks exist yet.
            </p>
            <p className="text-text-secondary mt-2">
              Would you like to create a new playbook?
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={handleEmptyCancel}>
              Cancel
            </Button>
            <Button color="primary" onPress={handleCreateNew}>
              Create New
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
};
